import classNames from 'classnames';
import Immutable from 'immutable';
import { noop, round } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import ArtboardLayoutContainer from 'components/ArtboardLayoutContainer';
import ArtboardLayoutContainerList from 'components/ArtboardLayoutContainerList';
import createDndScrollZone from 'components/DndScrollZone';
import LayoutDragHotspot from 'components/LayoutDragHotspot';
import LoadingOverlay from 'components/LoadingOverlay';
import { MIN_HEIGHT_FOR_MD_LAYOUT_SPINNER, MIN_HEIGHT_FOR_SM_LAYOUT_SPINNER, ProcessType, ProjectsConfig } from 'const';
import { getScale } from 'containers/App/selectors';
import ArtboardLayoutActions from 'containers/ArtboardLayoutActions';
import { isChildLayoutBeingUploaded as isChildLayoutBeingUploadedSelector } from 'containers/Layouts/selectors';
import * as Models from 'models';
import { createSmallVerticalStrength } from 'utils/dndScrollZone';
import { isMockDocument } from 'utils/documents';
import { getLayoutSpinnerSize } from 'utils/layouts/getLayoutSpinnerSize';
import { toPx } from 'utils/toPx';
import { useGroupLayoutDnD } from './dnd';
import { useGroupLayout } from './hooks';
import { groupLayoutSelector } from './selectors';
import styles from './styles.module.scss';

const DnDScrollZone = createDndScrollZone<React.HTMLAttributes<HTMLElement>>('div');

type Props = {
  getLayoutsRefs?: () => Immutable.Map<string, HTMLDivElement>;
  isSectionActive: boolean;
  layout: Models.GroupLayoutMap;
  position: number;
  saveLayoutRef?: (layoutId: string, element: HTMLDivElement) => void;
  section: Models.ExtendedSectionMap;
  ssi: Models.SSIMap | null;
  isReadOnly: boolean;
  dataTestId?: string;
  isLayoutBeingUploaded: boolean;
  isLastLayout: boolean;
  isCarouselItem: boolean;
};

const GroupLayout: React.FunctionComponent<Props> = (props) => {
  const {
    getLayoutsRefs,
    isLayoutBeingUploaded,
    isSectionActive,
    layout: groupLayout,
    position,
    saveLayoutRef = noop,
    section,
    ssi,
    isReadOnly,
    dataTestId = 'groupLayout',
    isLastLayout,
    isCarouselItem,
  } = props;
  const isChildLayoutBeingUploaded = useSelector(isChildLayoutBeingUploadedSelector(groupLayout.get('id')));

  const {
    documents,
    dragHotspotGroupLayoutId,
    dragHotspotPosition,
    moveableLayoutId,
    projectType,
    relationsByRegularLayoutId,
    layouts,
  } = useSelector(groupLayoutSelector);

  const { isOver, connectDragSource, connectDropTarget, isOverByAllowedItem } = useGroupLayoutDnD({
    isReadOnly,
    groupLayout,
    isLayoutBeingUploaded,
    sectionId: section.get('id'),
  });

  const {
    pinnedLayout,
    childLayouts,
    height,
    isDraggingLayout,
    isGroupLayoutWithOneChild,
    layoutDocumentName,
    layoutDocumentNumber,
    layoutId,
    layoutIds,
    onDragOver,
    onResize,
    onResizeEnd,
    container,
    pinnedLayoutRef,
    unpinnedLayoutsContainer,
  } = useGroupLayout({
    isOverByAllowedItem,
    getLayoutsRefs,
    isLayoutBeingUploaded,
    groupLayout,
    ssi,
    documents,
    layouts,
    relationsByRegularLayoutId,
    projectType,
  });

  const { allowScrollableLayout } = ProjectsConfig[projectType];
  const isMockGroupLayoutDocument = isMockDocument(documents.get(groupLayout.get('documentId')));

  const renderDragHotspot = (): JSX.Element | null => {
    if (!isOver || !isDraggingLayout() || !dragHotspotGroupLayoutId) {
      return null;
    }

    return <LayoutDragHotspot position={dragHotspotPosition} />;
  };

  const renderPinnedLayout = (): JSX.Element => (
    <>
      {dragHotspotPosition === 0 && renderDragHotspot()}
      <ArtboardLayoutContainer
        disableDragAndDelete={isGroupLayoutWithOneChild}
        documents={documents}
        groupLayoutId={layoutId}
        isFirst={true}
        isLastLayout={childLayouts.size === 1}
        isSectionActive={isSectionActive}
        key={pinnedLayout.get('id')} // use key because pinned layout could be reordered
        layout={pinnedLayout}
        layoutId={pinnedLayout.get('id')}
        layoutRelations={relationsByRegularLayoutId.get(pinnedLayout.get('id'))}
        moveableArtboardLayoutId={moveableLayoutId}
        position={0}
        saveLayoutRef={saveLayoutRef}
        sectionDisplayName={section.get('displayName')}
        sectionName={section.get('name')}
        sectionStyles={section.get('brandStyles')}
        sectionId={section.get('id')}
        ssi={ssi}
        dataTestId={`${dataTestId}-layoutContainer0`}
      />
    </>
  );

  const renderUnpinnedLayouts = (): JSX.Element => (
    <>
      {(!pinnedLayout || dragHotspotPosition > 0) && renderDragHotspot()}
      <ArtboardLayoutContainerList
        isReadOnly={isReadOnly}
        documents={documents}
        getLayoutsRefs={getLayoutsRefs}
        groupLayoutId={layoutId}
        isGroupLayoutWithOneChild={isGroupLayoutWithOneChild}
        isSectionActive={isSectionActive}
        layoutIds={layoutIds}
        layouts={childLayouts}
        moveableLayoutId={moveableLayoutId}
        relationsByRegularLayoutId={relationsByRegularLayoutId}
        saveLayoutRef={saveLayoutRef}
        section={section}
        skipFirstLayout={!!pinnedLayout}
        ssi={ssi}
        dataTestId={dataTestId}
      />
    </>
  );

  const scale = useSelector(getScale);
  const clientHeight = (container.current?.clientHeight ?? 0) * scale || MIN_HEIGHT_FOR_SM_LAYOUT_SPINNER;

  return connectDropTarget((
    <div
      className={classNames(styles.GroupLayout, {
        [styles.scrollable]: allowScrollableLayout,
        [styles.insideOfSSI]: !!ssi,
      })}
      style={{ order: position }}
      onDragOver={onDragOver}
      ref={el => saveLayoutRef(layoutId, el)}
      data-document-number={layoutDocumentNumber}
      data-testid={dataTestId}
    >
      <LoadingOverlay
        isLocked={isLayoutBeingUploaded ?? false}
        activeProcessType={clientHeight <= MIN_HEIGHT_FOR_MD_LAYOUT_SPINNER
          ? ProcessType.SAVE_LAYOUT_WITHOUT_MESSAGE
          : isChildLayoutBeingUploaded ? ProcessType.SAVE_REUSABLE_LAYOUT : ProcessType.SAVE_GROUPED_REUSABLE_LAYOUT
        }
        spinnerSize={getLayoutSpinnerSize(clientHeight)}
      />
      {!ssi && !isMockGroupLayoutDocument && <ArtboardLayoutActions
        disableDragAndDelete={isLastLayout && isCarouselItem}
        showActions={isOverByAllowedItem}
        hideButtons={isOverByAllowedItem}
        connectDragSource={connectDragSource}
        editMode={false}
        hint={layoutDocumentName}
        layout={groupLayout}
        isBeingUploaded={isLayoutBeingUploaded}
        isResizeAvailable={!!height}
        height={round(height)}
        handleDrag={onResize}
        handleDragStop={onResizeEnd}
        dataTestId={dataTestId}
      />}
      <DnDScrollZone
        verticalStrength={createSmallVerticalStrength}
        className={classNames(styles.container, { [styles.scrollable]: allowScrollableLayout })}
        style={{ maxHeight: (allowScrollableLayout && !ssi) ? toPx(height) : 'none' }}
        ref={container}
      >
        {
          pinnedLayout
            ?
            <>
              <div ref={pinnedLayoutRef}>{renderPinnedLayout()}</div>
              <DnDScrollZone
                verticalStrength={createSmallVerticalStrength}
                className={classNames(styles.unpinnedLayouts, {
                  [styles.scrollable]: allowScrollableLayout,
                })}
                ref={unpinnedLayoutsContainer}
              >
                {renderUnpinnedLayouts()}
              </DnDScrollZone>
            </>
            :
            renderUnpinnedLayouts()
        }
      </DnDScrollZone>
    </div>
  ));
};

export default GroupLayout;
